import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal, easeOut } from "@/components/common/Reveal";
import { useLead } from "@/components/common/LeadModal";

const STEPS = [
  {
    n: "01",
    title: "Choisissez",
    highlight: "votre formation",
    text: "Parcourez notre catalogue — transport de marchandises, de voyageurs ou enseignement de la conduite — et identifiez la formation adaptée à votre projet. Nos conseillers vous orientent.",
    cta: { label: "Voir les formations", to: "/nos-formations" },
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/iax7c3mj_pexels-mart-production-8472987.webp",
  },
  {
    n: "02",
    title: "Financez",
    highlight: "votre parcours",
    text: "CPF, France Travail, OPCO ou financement entreprise : nos équipes vous accompagnent pour constituer votre dossier et optimiser votre budget avec le dispositif le plus adapté.",
    cta: { label: "Voir les financements", to: "/financements" },
    image: "https://images.pexels.com/photos/3760067/pexels-photo-3760067.jpeg?auto=compress&cs=tinysrgb&w=1100",
  },
  {
    n: "03",
    title: "Formez-vous",
    highlight: "dans nos centres",
    text: "Présentiel, simulateurs haut de gamme et véhicules en double commande dans nos centres de Palaiseau, Noisy-le-Sec et Meaux, avec des formateurs experts du secteur.",
    cta: { label: "Nos centres", to: "/nos-centres" },
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/pa06snjo_pexels-mikhail-nilov-9300726.webp",
  },
  {
    n: "04",
    title: "Obtenez",
    highlight: "votre qualification",
    text: "Réussissez votre examen et décrochez votre permis, votre Carte de Qualification de Conducteur ou votre titre professionnel reconnu. Bienvenue dans le métier !",
    cta: { label: "S'inscrire", lead: "inscription" },
    image: "https://customer-assets-v7afamib.emergentagent.net/job_educaser-dev/artifacts/no74cuzr_pexels-rdne-9034969.webp",
  },
];

export default function Etapes() {
  const { openLead } = useLead();

  return (
    <section className="bg-white py-24 lg:py-32" data-testid="etapes">
      <div className="mx-auto max-w-[1400px] px-5 lg:px-8">
        <Reveal className="mx-auto max-w-2xl text-center">
          <h2 className="font-display text-4xl font-black uppercase leading-[0.98] tracking-[-0.03em] text-brand-blue sm:text-5xl">
            Votre parcours de formation
          </h2>
          <p className="mt-4 text-lg text-slate-500">Les 4 étapes clés pour réussir avec Educaser</p>
        </Reveal>

        <div className="mt-16 space-y-8">
          {STEPS.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <motion.div
                key={s.n}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.7, ease: easeOut }}
                className="grid grid-cols-1 overflow-hidden rounded-3xl lg:grid-cols-2"
                data-testid={`etape-${s.n}`}
              >
                {/* Text panel */}
                <div className={`flex flex-col justify-center bg-brand-blue p-8 sm:p-10 lg:p-16 ${reversed ? "lg:order-2" : ""}`}>
                  <span className="font-display text-lg font-bold text-white/50">{s.n}</span>
                  <h3 className="mt-4 hyphens-auto break-words font-display text-3xl font-black uppercase leading-[1.05] tracking-[-0.02em] text-white sm:text-4xl lg:text-5xl" lang="fr">
                    {s.title}<br />
                    <span className="text-brand-red-light">{s.highlight}</span>
                  </h3>
                  <p className="mt-6 max-w-md leading-relaxed text-white/75">{s.text}</p>
                  <div className="mt-8">
                    {s.cta.to ? (
                      <Link
                        to={s.cta.to}
                        className="group inline-flex items-center gap-2 rounded-full bg-brand-red px-7 py-3.5 font-bold text-white transition-colors hover:bg-brand-red-light"
                        data-testid={`etape-cta-${s.n}`}
                      >
                        {s.cta.label}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </Link>
                    ) : (
                      <button
                        onClick={() => openLead(s.cta.lead)}
                        className="group inline-flex items-center gap-2 rounded-full bg-brand-red px-7 py-3.5 font-bold text-white transition-colors hover:bg-brand-red-light"
                        data-testid={`etape-cta-${s.n}`}
                      >
                        {s.cta.label}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Image panel */}
                <div className={`relative min-h-[280px] lg:min-h-[420px] ${reversed ? "lg:order-1" : ""}`}>
                  <img src={s.image} alt={`${s.title} ${s.highlight}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
